import React, { TextareaHTMLAttributes, ReactNode, forwardRef } from 'react';
import TextArea from './TextArea';
import FormControlWrapper from './FormControlWrapper';
import controlHintIds from './controlHintIds';
import { InputVariant } from './InputVariant';
import useCharacterCount from './useCharacterCount';

export interface TextAreaControlProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  id: string;
  label: string;
  className?: string;
  errorMessage?: string;
  hint?: string;
  labelHint?: ReactNode;
  note?: ReactNode;
  success?: boolean;
  characterCountMax?: number;
  variant?: InputVariant;
  requiredLabel?: ReactNode;
}

/**
 * The TextAreaControl based on [The USWDS Text input component](https://designsystem.digital.gov/components/form-controls/#text-input)
 *
 * note: Configured `requiredLabel` text is appended to the labelhint if required attribute is true.
 */
const TextAreaControl = forwardRef<HTMLTextAreaElement, TextAreaControlProps>(
  (
    {
      id,
      label,
      className,
      errorMessage,
      hint,
      labelHint,
      note,
      characterCountMax,
      onChange,
      value,
      maxLength,
      'aria-describedby': ariaDescribedBy,
      required,
      requiredLabel,
      ...rest
    }: TextAreaControlProps,
    ref,
  ): JSX.Element => {
    const [characterCount, changeHandler] = useCharacterCount<HTMLTextAreaElement>(value, characterCountMax, onChange);
    const { labelHintId, hintId, errorId, characterCountId, describedBy } = controlHintIds(
      id,
      labelHint,
      hint,
      errorMessage,
      characterCountMax,
      ariaDescribedBy,
    );
    return (
      <FormControlWrapper
        id={id}
        label={label}
        labelHint={labelHint}
        labelHintId={labelHintId}
        hint={hint}
        hintId={hintId}
        note={note}
        errorId={errorId}
        errorMessage={errorMessage}
        characterCountMax={characterCountMax}
        characterCount={characterCount}
        characterCountId={characterCountId}
        required={required}
        requiredLabel={requiredLabel}
      >
        <TextArea
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...rest}
          aria-describedby={describedBy}
          id={id}
          ref={ref}
          value={value}
          className="text-area-control__text-area"
          errorId={errorId}
          maxLength={characterCountMax ? undefined : maxLength}
          onChange={changeHandler}
          aria-required={required}
          required={required}
        />
      </FormControlWrapper>
    );
  },
);

export default TextAreaControl;
